// src/features/game/components/GameCard.tsx

import RouteConfig from '../../../routes/RouteConfig';

import React, {
  useState,
} from 'react';

import {
  Pause,
  Play,
} from 'lucide-react';

import {
  useNavigate,
} from 'react-router-dom';

import type {
  GameStatus,
  PlayGame,
} from '../types/game.types';

import {
  pauseGame,
  resumeGame,
  startGame,
} from '../services/gameService';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import {
  calculateElapsedTime,
  formatRemainingTime,
} from '../../../shared/utils/timeUtils';

import {
  formatCurrency,
  formatNumber,
} from '../../../shared/utils/formatUtils';

interface GameCardProps {
  game: PlayGame;

  onRefresh?: () => void;
}

const getGameStatusLabel = (
  status: GameStatus,
): string => {
  switch (status) {
    case 'WAIT':
      return '대기';

    case 'RUNNING':
    case 'STARTED':
      return '진행 중';

    case 'PAUSED':
      return '일시정지';

    case 'FINISHED':
      return '종료';

    default:
      return status;
  }
};

const getGameStatusClassName = (
  status: GameStatus,
): string => {
  switch (status) {
    case 'WAIT':
      return 'bg-gray-100 text-gray-700';

    case 'RUNNING':
    case 'STARTED':
      return 'bg-green-100 text-green-700';

    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-700';

    case 'FINISHED':
      return 'bg-red-100 text-red-700';

    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const GameCard:
  React.FC<GameCardProps> = ({
    game,
    onRefresh,
  }) => {
  const navigate =
    useNavigate();

  const modal =
    useModal();

  const [
    loading,
    setLoading,
  ] = useState(false);

  const isFinished =
    game.gameStatus ===
    'FINISHED';

  const isRunning =
    game.gameStatus ===
      'RUNNING' ||
    game.gameStatus ===
      'STARTED';

  const isPaused =
    game.gameStatus ===
    'PAUSED';

  const isWaiting =
    game.gameStatus ===
    'WAIT';

  /**
   * 게임 시작
   */
  const handleStartGame =
    async (): Promise<void> => {
      const confirmed =
        await modal.confirm(
          '게임을 시작하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(
          true,
        );

        await startGame(
          game.playGameId,
        );

        onRefresh?.();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 시작 실패:',
          error,
        );

        await modal.alert(
          '게임 시작에 실패했습니다.',
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  /**
   * 게임 일시정지
   */
  const handlePauseGame =
    async (): Promise<void> => {
      const confirmed =
        await modal.confirm(
          '게임을 일시정지하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(
          true,
        );

        await pauseGame(
          game.playGameId,
        );

        onRefresh?.();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 일시정지 실패:',
          error,
        );

        await modal.alert(
          '게임 일시정지에 실패했습니다.',
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  /**
   * 게임 재개
   */
  const handleResumeGame =
    async (): Promise<void> => {
      const confirmed =
        await modal.confirm(
          '게임을 재개하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(
          true,
        );

        await resumeGame(
          game.playGameId,
        );

        onRefresh?.();
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 재개 실패:',
          error,
        );

        await modal.alert(
          '게임 재개에 실패했습니다.',
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  /**
   * 상세 페이지 이동
   */
const handleViewDetails =
  (): void => {
    localStorage.setItem(
      `game_${game.playGameId}_status`,
      game.gameStatus,
    );

    navigate(
      RouteConfig.Management.GameDetail.to(
        game.playGameId,
      ),
    );
  };

  const cardStyle =
    isFinished
      ? 'bg-gray-200 opacity-75'
      : 'bg-gradient-to-br from-main-bright to-main';

  return (
    <div
      className={`${cardStyle} rounded-lg p-6 shadow-lg`}
    >
      {/* 상태 */}
      <div className="mb-4">
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getGameStatusClassName(
            game.gameStatus,
          )}`}
        >
          {getGameStatusLabel(
            game.gameStatus,
          )}
        </span>
      </div>

      {/* 게임명 */}
      <h3
        className={`mb-4 text-20-semibold ${
          isFinished
            ? 'text-gray-700'
            : 'text-white'
        }`}
      >
        {game.gameName}

        {game.gameSubtitle
          ? ` - ${game.gameSubtitle}`
          : ''}
      </h3>

      {/* 게임 정보 */}
      <div
        className={`mb-4 space-y-2 ${
          isFinished
            ? 'text-gray-700'
            : 'text-white'
        }`}
      >
        <div className="flex justify-between text-16-regular">
          <span>
            플레이어
          </span>

          <span className="font-medium">
            {
              game.nowPeople
            }
            /
            {
              game.totalPeople
            }
          </span>
        </div>

        {!isFinished &&
          game.startAt && (
            <div className="flex justify-between text-16-regular">
              <span>
                경과 시간
              </span>

              <span className="font-medium">
                {calculateElapsedTime(
                  game.startAt,
                )}
              </span>
            </div>
          )}

        {!isFinished && (
          <div className="flex justify-between text-16-regular">
            <span>
              총 스택
            </span>

            <span className="font-medium">
              {formatNumber(
                game.totalStack,
              )}{' '}
              칩
            </span>
          </div>
        )}

        <div className="flex justify-between text-16-regular">
          <span>
            바이인
          </span>

          <span className="font-medium">
            {formatCurrency(
              game.buyinPrice,
            )}
          </span>
        </div>

        {(isRunning ||
          isPaused) && (
          <>
            {game.currentLevel !==
              null && (
              <div className="flex justify-between text-16-regular">
                <span>
                  현재 레벨
                </span>

                <span className="font-medium">
                  Level{' '}
                  {
                    game.currentLevel
                  }
                </span>
              </div>
            )}

            {game.currentBlind && (
              <div className="flex justify-between gap-3 text-16-regular">
                <span>
                  블라인드
                </span>

                <span className="text-right font-medium">
                  {
                    game
                      .currentBlind
                      .smallBlind
                  }
                  /
                  {
                    game
                      .currentBlind
                      .bigBlind
                  }

                  {game.nextBlind && (
                    <>
                      {' → '}

                      {
                        game
                          .nextBlind
                          .smallBlind
                      }
                      /
                      {
                        game
                          .nextBlind
                          .bigBlind
                      }
                    </>
                  )}
                </span>
              </div>
            )}

            {game.levelRemainingSeconds !==
              null && (
              <div className="flex justify-between text-16-regular">
                <span>
                  남은 시간
                </span>

                <span className="text-18-semibold font-medium">
                  {formatRemainingTime(
                    game.levelRemainingSeconds,
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* 액션 */}
      <div className="space-y-2">
        {isWaiting && (
          <button
            type="button"
            onClick={() =>
              void handleStartGame()
            }
            disabled={
              loading
            }
            className="w-full rounded-lg bg-black py-3 text-16-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading
              ? '처리 중...'
              : '게임 시작'}
          </button>
        )}

        {isRunning && (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center justify-center rounded-lg bg-black py-3 text-16-medium text-white">
              게임 중
            </div>

            <button
              type="button"
              onClick={() =>
                void handlePauseGame()
              }
              disabled={
                loading
              }
              className="rounded-lg bg-black p-3 text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              title="일시정지"
              aria-label="게임 일시정지"
            >
              <Pause
                size={20}
              />
            </button>
          </div>
        )}

        {isPaused && (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center justify-center rounded-lg bg-black py-3 text-16-medium text-white">
              일시정지
            </div>

            <button
              type="button"
              onClick={() =>
                void handleResumeGame()
              }
              disabled={
                loading
              }
              className="rounded-lg bg-black p-3 text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              title="재개"
              aria-label="게임 재개"
            >
              <Play
                size={20}
              />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={
            handleViewDetails
          }
          className={`w-full rounded-lg py-3 text-16-medium transition-colors ${
            isFinished
              ? 'border border-gray-400 bg-white text-gray-700 hover:bg-gray-100'
              : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'
          }`}
        >
          상세 보기
        </button>
      </div>
    </div>
  );
};

export default GameCard;